import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { FaCreditCard, FaTrash, FaPlus, FaLock, FaCheck } from 'react-icons/fa';
import api from '../../../services/api';

// Charger Stripe avec la clé publique
const stripePromise = loadStripe(
  process.env.REACT_APP_ENV === "production"
    ? "pk_live_51PLuQiHzL8gubNCfOBaaohZ0Vwz2Rti3HVD5YSdxui9saiRuPA1T0XRfNKEfvmlxSeKBHJX3uGBcOGdd769RoChF00t2XrnVLW"
    : "pk_test_51PLuQiHzL8gubNCfacSYt0jDJM3KgLsMpR3lYmVdKJTu57m1WCbR9NUnIIbKZeLt7Jlf8rZ1CQyH57REdDy6Z5wU00h2owk64H"
);

// Style pour le CardElement
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: true,
};

// Composant interne pour le formulaire de carte
const CardForm = ({ onCardSaved, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Créer un SetupIntent
      const { data } = await api.post('/store-sms/setup-intent');
      const { clientSecret } = data;

      // Confirmer le SetupIntent avec les infos de la carte
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
      } else if (setupIntent.status === 'succeeded') {
        onCardSaved();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement de la carte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-gray-300 rounded-lg bg-white">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      
      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || loading}
          className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
            loading
              ? 'bg-gray-300 text-gray-500'
              : 'bg-fideliz-blue text-white hover:bg-fideliz-blue hover:opacity-80'
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              Enregistrement...
            </>
          ) : (
            <>
              <FaLock className="w-4 h-4" />
              Enregistrer la carte
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Annuler
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
        <FaLock className="w-3 h-3" />
        Paiement sécurisé par Stripe
      </p>
    </form>
  );
};

// Composant principal
const CardPaymentForm = ({ pack, onSuccess, onCancel }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [requires3DS, setRequires3DS] = useState(null);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/store-sms/payment-methods');
      setPaymentMethods(data.paymentMethods || []);
      if (data.paymentMethods?.length > 0) {
        setSelectedCard(data.paymentMethods[0].id);
      }
    } catch (err) {
      console.error('Erreur chargement cartes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Supprimer cette carte ?')) return;
    
    try {
      await api.delete(`/store-sms/payment-methods/${cardId}`);
      setPaymentMethods(paymentMethods.filter((c) => c.id !== cardId));
      if (selectedCard === cardId) {
        setSelectedCard(paymentMethods[0]?.id || null);
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleCardSaved = () => {
    setShowAddCard(false);
    loadPaymentMethods();
  };

  const handlePay = async () => {
    if (!selectedCard || !pack) return;

    setPaying(true);
    setError('');

    try {
      const { data } = await api.post('/store-sms/pay', {
        packId: pack.id,
        paymentMethodId: selectedCard,
      });

      if (data.success && data.status === 'succeeded') {
        onSuccess(data.tokens, data.tokensAdded);
      } else if (data.status === 'requires_action') {
        // 3D Secure requis
        setRequires3DS({
          clientSecret: data.clientSecret,
          purchaseId: data.purchaseId,
        });
      } else {
        setError(data.message || 'Paiement non complété');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du paiement');
    } finally {
      setPaying(false);
    }
  };

  // Composant pour gérer le 3DS
  const Handle3DS = ({ clientSecret, purchaseId }) => {
    const stripe = useStripe();
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
      const confirm3DS = async () => {
        if (!stripe) return;

        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);

        if (error) {
          setError(error.message);
          setRequires3DS(null);
        } else if (paymentIntent.status === 'succeeded') {
          // Confirmer côté serveur
          try {
            const { data } = await api.post('/store-sms/confirm-payment', {
              purchaseId,
              paymentIntentId: paymentIntent.id,
            });
            if (data.success) {
              onSuccess(data.tokens, data.tokensAdded);
            }
          } catch (err) {
            setError('Erreur lors de la confirmation');
          }
        }
        setProcessing(false);
      };

      confirm3DS();
    }, [stripe, clientSecret, purchaseId]);

    if (processing) {
      return (
        <div className="flex flex-col items-center justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-fideliz-blue border-t-transparent mb-4"></div>
          <p className="text-gray-600">Vérification 3D Secure en cours...</p>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-fideliz-blue border-t-transparent"></div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="space-y-5">
        {/* Résumé de l'achat */}
        <div className="bg-fideliz-blue/5 border border-fideliz-blue/20 rounded-lg p-4">
          <h3 className="font-bold text-lg">{pack.name}</h3>
          <div className="flex justify-between items-center mt-2">
            <span className="text-gray-600">{pack.tokens} SMS</span>
            <div className="text-right">
              <span className="text-2xl font-bold text-fideliz-blue">
                {parseFloat(pack.price_ttc || pack.price * 1.2).toFixed(2)} € TTC
              </span>
              <p className="text-xs text-gray-500">
                {parseFloat(pack.price_ht || pack.price).toFixed(2)} € HT + TVA 20%
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {requires3DS ? (
          <Handle3DS
            clientSecret={requires3DS.clientSecret}
            purchaseId={requires3DS.purchaseId}
          />
        ) : showAddCard ? (
          <CardForm onCardSaved={handleCardSaved} onCancel={() => setShowAddCard(false)} />
        ) : (
          <>
            {/* Liste des cartes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Choisissez une carte
              </label>

              {paymentMethods.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  Aucune carte enregistrée
                </p>
              ) : (
                <div className="space-y-2">
                  {paymentMethods.map((card) => (
                    <div
                      key={card.id}
                      onClick={() => setSelectedCard(card.id)}
                      className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition ${
                        selectedCard === card.id
                          ? 'border-fideliz-blue bg-fideliz-blue/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <FaCreditCard className="text-gray-500 w-6 h-6" />
                        <div>
                          <p className="font-medium capitalize">
                            {card.brand} •••• {card.last4}
                          </p>
                          <p className="text-sm text-gray-500">
                            Expire {card.expMonth}/{card.expYear}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedCard === card.id && (
                          <FaCheck className="text-fideliz-blue" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCard(card.id);
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Bouton ajouter une carte */}
              <button
                onClick={() => setShowAddCard(true)}
                className="mt-3 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-fideliz-blue hover:text-fideliz-blue flex items-center justify-center gap-2 transition"
              >
                <FaPlus className="w-4 h-4" />
                Ajouter une nouvelle carte
              </button>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handlePay}
                disabled={!selectedCard || paying}
                className={`flex-1 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 ${
                  !selectedCard || paying
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {paying ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Paiement en cours...
                  </>
                ) : (
                  <>
                    <FaLock className="w-5 h-5" />
                    Payer {parseFloat(pack.price_ttc || pack.price * 1.2).toFixed(2)} €
                  </>
                )}
              </button>
              <button
                onClick={onCancel}
                className="px-6 py-4 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300"
              >
                Retour
              </button>
            </div>
          </>
        )}
      </div>
    </Elements>
  );
};

export default CardPaymentForm;
